import {
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getNonEmptyCohorts } from '../../api/Cohort';
import { Cohort, CohortId } from '../../api/Cohort/types/Cohort';
import matchingRoundsAPI, { MatchingRoundId } from '../../api/matching-rounds';
import venturesAPI from '../../api/ventures';
import { Venture, VentureId } from '../../api/ventures/types/Venture';
import { AlertState, Button, Select } from '../../components/common';
import { ActionsDialog } from '../../components/common/Confirmation/ActionsDialog';
import { COLORS } from '../../theme/variables';
import CohortIcon from '../ventures/cohort-icon';

const MAX_SELECTED_VENTURES = 500;

// region Types

export interface VentureSubgroupSelectorHandle {
  getSelectedCount(): number;
}

interface VentureSubgroupSelectorProps {
  roundId: MatchingRoundId;
}

export type CohortFilter = CohortId | 'all';

interface FilterState {
  search: string;
  cohort: CohortFilter;
  // Deferred values for rendering list
  deferredSearch: string;
  deferredCohort: CohortFilter;
}

function useFilteredVentures(ventures: Venture[], defaultFilter: FilterState) {
  const [filter, setFilter] = useState<FilterState>(defaultFilter);
  const [checkedIds, setCheckedIds] = useState<Set<VentureId>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Update deferred values after delay/tick
  const setDeferred = useCallback((updates: Partial<FilterState>) => {
    // Immediate update for UI inputs
    setFilter((prev) => {
      const next = { ...prev, ...updates };
      // Schedule deferred update
      if (timerRef.current) clearTimeout(timerRef.current);
      requestAnimationFrame(() => {
        timerRef.current = setTimeout(() => {
          setFilter((p) => ({
            ...p,
            deferredSearch: next.search,
            deferredCohort: next.cohort,
          }));
        }, 0);
      });
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    return filterVentures(
      ventures,
      filter.deferredSearch,
      filter.deferredCohort,
    );
  }, [ventures, filter.deferredSearch, filter.deferredCohort]);

  const stats = useMemo(() => {
    const checkedCount = filtered.filter((v) => checkedIds.has(v.id)).length;
    const allFilteredChecked =
      filtered.length > 0 && filtered.every((v) => checkedIds.has(v.id));
    const someFilteredChecked =
      filtered.some((v) => checkedIds.has(v.id)) && !allFilteredChecked;

    return { checkedCount, allFilteredChecked, someFilteredChecked };
  }, [filtered, checkedIds]);

  return useMemo(
    () => ({
      filter,
      setSearch: (s: string) => setDeferred({ search: s }),
      setCohort: (c: CohortFilter) => setDeferred({ cohort: c }),
      checkedIds,
      setCheckedIds,
      filtered,
      stats,
    }),
    [filter, setDeferred, checkedIds, filtered, stats],
  );
}

// endregion

// region Styles

const useStyles = makeStyles((theme) => ({
  panels: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
    border: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    borderRadius: 8,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: '1fr 1fr',
    },
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
    '&:first-child': {
      borderRight: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    },
    [theme.breakpoints.down('xs')]: {
      '&:first-child': {
        borderRight: 'none',
        borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
      },
    },
  },
  panelHeader: {
    padding: '10px 12px',
    background: COLORS.COLOR_ADDITIONAL_GENERAL_BACKGROUND,
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    flexShrink: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    marginBottom: 6,
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.COLOR_TEXT_BASE,
  },
  counter: {
    fontSize: 12,
    color: COLORS.COLOR_GRAY_BASE,
  },
  counterBold: {
    fontWeight: 700,
    color: COLORS.COLOR_BLUE_BASE,
  },
  filterRow: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
  },
  searchField: {
    flex: 1, // Takes available space
    '& .MuiInputBase-root': {
      background: COLORS.COLOR_ADDITIONAL_WHITE,
    },
    '& .MuiInputBase-input': {
      height: 38,
      paddingTop: 10,
      paddingBottom: 10,
      boxSizing: 'border-box',
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: 4,
    },
  },
  cohortSelect: {
    marginTop: 0, // Removed top margin
    width: 160, // Fixed width for select
    flexShrink: 0,
    '& > div': {
      minHeight: 38,
    },
    // Hide cohort dropdowns when side-by-side but narrow (below lg).
    // Show again below xs (600px) where panels stack vertically.
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
  selectAllRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 14,
    color: COLORS.COLOR_GRAY_DARKENED,
    padding: '2px 12px',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    flexShrink: 0,
    '& .MuiCheckbox-root': {
      padding: 4,
    },
  },
  selectAllRowDisabled: {
    opacity: 0.5,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: 4,
    minHeight: 0,
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      background: COLORS.COLOR_GRAY_LIGHTENED_20,
      borderRadius: 3,
      '&:hover': {
        background: COLORS.COLOR_GRAY_BASE,
      },
    },
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 12,
    cursor: 'pointer',
    transition: 'background 0.1s',
    userSelect: 'none',
    '&:hover': {
      background: COLORS.COLOR_BLUE_LIGHTENED_50,
    },
    '& .MuiCheckbox-root': {
      padding: 4,
    },
  },
  itemName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: COLORS.COLOR_TEXT_BASE,
  },
  itemCohort: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    marginLeft: 4,
    marginRight: 4,
  },
  actionBtn: {
    opacity: 0,
    transition: 'opacity 0.1s',
    padding: 2,
    '$listItem:hover &': {
      opacity: 1,
    },
  },
  actionBtnAdd: {
    color: COLORS.COLOR_BLUE_BASE,
  },
  actionBtnRemove: {
    color: COLORS.COLOR_RED_BASE,
  },
  bulkBar: {
    display: 'flex',
    gap: 8,
    padding: '8px 12px',
    borderTop: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    background: COLORS.COLOR_ADDITIONAL_GENERAL_BACKGROUND,
    flexShrink: 0,
    alignItems: 'center',
  },
  bulkBtnAdd: {
    fontSize: 14,
    padding: '6px 16px',
    borderRadius: 4,
    border: 'none',
    background: COLORS.COLOR_BLUE_BASE,
    color: COLORS.COLOR_ADDITIONAL_WHITE,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    lineHeight: 1.75,
    textTransform: 'none' as const,
    letterSpacing: '0.02857em',
    boxShadow:
      '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    '&:hover': {
      background: COLORS.COLOR_BLUE_DARKENED_10,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
      boxShadow: 'none',
    },
  },
  bulkBtnSecondary: {
    fontSize: 14,
    padding: '5px 15px',
    borderRadius: 4,
    border: `1px solid ${COLORS.COLOR_BLUE_BASE}`,
    background: COLORS.COLOR_ADDITIONAL_WHITE,
    color: COLORS.COLOR_BLUE_BASE,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    lineHeight: 1.75,
    textTransform: 'none' as const,
    letterSpacing: '0.02857em',
    '&:hover': {
      background: COLORS.COLOR_BLUE_LIGHTENED_50,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  listLoader: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
  },
  emptyMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    fontSize: 12,
    color: COLORS.COLOR_GRAY_BASE,
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    fontSize: 12,
    color: COLORS.COLOR_RED_BASE,
  },
}));

// endregion

// region Helpers

async function loadAllActiveVentures(): Promise<Venture[]> {
  const ventures = await venturesAPI.getActiveVentures();
  return ventures.sort((a, b) => a.ventureName.localeCompare(b.ventureName));
}

interface DebouncedSearchInputProps {
  placeholder: string;
  onDebouncedChange: (value: string) => void;
  delay?: number;
  className?: string;
  testId?: string;
}

const DebouncedSearchInput = React.memo(function DebouncedSearchInput({
  placeholder,
  onDebouncedChange,
  delay = 500,
  className,
  testId,
}: DebouncedSearchInputProps) {
  const [localValue, setLocalValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setLocalValue(val);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onDebouncedChange(val), delay);
    },
    [onDebouncedChange, delay],
  );

  const handleClear = useCallback(() => {
    setLocalValue('');
    if (timerRef.current) clearTimeout(timerRef.current);
    onDebouncedChange('');
  }, [onDebouncedChange]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <TextField
      className={className}
      value={localValue}
      onChange={handleChange}
      placeholder={placeholder}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: localValue ? (
          <InputAdornment position='end'>
            <IconButton
              size='small'
              onClick={handleClear}
              aria-label='Clear search'
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      inputProps={{ 'data-testid': testId }}
      size='small'
      variant='outlined'
    />
  );
});

export function filterVentures(
  ventures: Venture[],
  search: string,
  cohortFilter: CohortFilter,
): Venture[] {
  let filtered = ventures;

  if (cohortFilter !== 'all') {
    filtered = filtered.filter((v) => v.cohortId === cohortFilter);
  }

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter((v) => v.ventureName.toLowerCase().includes(q));
  }

  return filtered;
}

// endregion

// region Memoized list components

interface VentureListItemProps {
  ventureId: VentureId;
  ventureName: string;
  cohortName: string;
  checked: boolean;
  onToggleCheck: (id: VentureId, shiftKey: boolean) => void;
  onAction: (id: VentureId) => void;
  actionDisabled?: boolean;
  itemTestId: string;
  actionTestId: string;
  actionLabel: string;
  variant: 'add' | 'remove';
}

const VentureListItem = React.memo(function VentureListItem({
  ventureId,
  ventureName,
  cohortName,
  checked,
  onToggleCheck,
  onAction,
  actionDisabled,
  itemTestId,
  actionTestId,
  actionLabel,
  variant,
}: VentureListItemProps) {
  const classes = useStyles();

  const isAdd = variant === 'add';
  const actionBtnClass = cn(
    classes.actionBtn,
    isAdd ? classes.actionBtnAdd : classes.actionBtnRemove,
  );

  return (
    <div
      className={classes.listItem}
      onClick={(e) => onToggleCheck(ventureId, e.shiftKey)}
      data-testid={itemTestId}
    >
      <Checkbox
        color='primary'
        size='small'
        checked={checked}
        onChange={() => {
          /* controlled by onClick */
        }}
        onClick={(e) => {
          e.stopPropagation();
          onToggleCheck(ventureId, e.shiftKey);
        }}
      />
      <span className={classes.itemName}>{ventureName}</span>
      {cohortName && (
        <span className={classes.itemCohort}>
          <CohortIcon label={cohortName} />
        </span>
      )}
      <IconButton
        className={actionBtnClass}
        size='small'
        disabled={actionDisabled}
        onClick={(e) => {
          e.stopPropagation();
          onAction(ventureId);
        }}
        aria-label={actionLabel}
        data-testid={actionTestId}
      >
        {isAdd ? <AddIcon fontSize='small' /> : <CloseIcon fontSize='small' />}
      </IconButton>
    </div>
  );
});

interface VentureListProps {
  ventures: Venture[];
  checkedIds: Set<VentureId>;
  cohortMap: Map<CohortId, string>;
  onToggleCheck: (id: VentureId, shiftKey: boolean) => void;
  onAction: (id: VentureId) => void;
  actionDisabled?: boolean;
  emptyMessage: string;
  listTestId: string;
  itemTestIdPrefix: string;
  actionTestIdPrefix: string;
  actionLabel: string;
  variant: 'add' | 'remove';
}

const VentureList = React.memo(function VentureList({
  ventures,
  checkedIds,
  cohortMap,
  onToggleCheck,
  onAction,
  actionDisabled,
  emptyMessage,
  listTestId,
  itemTestIdPrefix,
  actionTestIdPrefix,
  actionLabel,
  variant,
}: VentureListProps) {
  const classes = useStyles();

  if (ventures.length === 0) {
    return (
      <div className={classes.list} data-testid={listTestId}>
        <div className={classes.emptyMessage}>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className={classes.list} data-testid={listTestId}>
      {ventures.map((venture) => (
        <VentureListItem
          key={venture.id}
          ventureId={venture.id}
          ventureName={venture.ventureName}
          cohortName={
            venture.cohortId ? cohortMap.get(venture.cohortId) || '' : ''
          }
          checked={checkedIds.has(venture.id)}
          onToggleCheck={onToggleCheck}
          onAction={onAction}
          actionDisabled={actionDisabled}
          itemTestId={`${itemTestIdPrefix}${venture.id}`}
          actionTestId={`${actionTestIdPrefix}${venture.id}`}
          actionLabel={actionLabel}
          variant={variant}
        />
      ))}
    </div>
  );
});

// endregion

const VentureSubgroupSelector = React.memo(
  forwardRef<VentureSubgroupSelectorHandle, VentureSubgroupSelectorProps>(
    function VentureSubgroupSelector({ roundId }, ref) {
      const classes = useStyles();
      const { enqueueSnackbar } = useSnackbar();

      // Data state
      const [allVentures, setAllVentures] = useState<Venture[]>([]);
      const [cohorts, setCohorts] = useState<Cohort[]>([]);
      const [isLoading, setIsLoading] = useState(true);
      const [loadError, setLoadError] = useState(false);

      // Selection state
      const [selectedIds, setSelectedIds] = useState<Set<VentureId>>(new Set());
      const selectedIdsRef = useRef(selectedIds);
      selectedIdsRef.current = selectedIds;

      // Toggle Helpers
      const toggleSelection = useCallback(
        (id: VentureId, isSelected: boolean) => {
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (isSelected) next.add(id);
            else next.delete(id);
            return next;
          });
        },
        [],
      );

      // Derived lists (before filtering)
      const availableRaw = useMemo(
        () => allVentures.filter((v) => !selectedIds.has(v.id)),
        [allVentures, selectedIds],
      );

      const selectedRaw = useMemo(
        () => allVentures.filter((v) => selectedIds.has(v.id)),
        [allVentures, selectedIds],
      );

      // Hooks for panels
      const left = useFilteredVentures(availableRaw, {
        search: '',
        cohort: 'all',
        deferredSearch: '',
        deferredCohort: 'all',
      });

      const right = useFilteredVentures(selectedRaw, {
        search: '',
        cohort: 'all',
        deferredSearch: '',
        deferredCohort: 'all',
      });

      // Refs for panel state used inside callbacks — keeps callbacks stable
      // even though the `left`/`right` objects change on every selection.
      const leftRef = useRef(left);
      leftRef.current = left;
      const rightRef = useRef(right);
      rightRef.current = right;

      // Data Loading
      useEffect(() => {
        let cancelled = false;

        async function load() {
          try {
            setIsLoading(true);
            setLoadError(false);
            const [cohortsData, venturesData, roundVentures] =
              await Promise.all([
                getNonEmptyCohorts(),
                loadAllActiveVentures(),
                matchingRoundsAPI.getVentures(roundId),
              ]);

            if (cancelled) return;

            setCohorts(cohortsData.filter((c) => c.status === 'ACTIVE'));
            setAllVentures(venturesData);
            setSelectedIds(new Set(roundVentures.map((rv) => rv.ventureId)));
          } catch (e: unknown) {
            if (cancelled) return;
            setLoadError(true);
            enqueueSnackbar('Failed to load ventures. Please try again.', {
              variant: 'error',
            });
            console.error('Failed to load venture subgroup data:', e);
          } finally {
            if (!cancelled) setIsLoading(false);
          }
        }
        load();
        return () => {
          cancelled = true;
        };
      }, [roundId, enqueueSnackbar]);

      // Auto-save logic
      const saveChange = useCallback(
        async (added: VentureId[], removed: VentureId[]) => {
          try {
            const promises: Promise<unknown>[] = [];
            if (added.length > 0)
              promises.push(matchingRoundsAPI.addVentures(roundId, added));
            if (removed.length > 0)
              promises.push(matchingRoundsAPI.removeVentures(roundId, removed));
            await Promise.all(promises);
          } catch (e) {
            console.error('Failed to auto-save:', e);
            enqueueSnackbar('Failed to save changes. Reverting...', {
              variant: 'error',
            });
            // Revert
            setSelectedIds((prev) => {
              const next = new Set(prev);
              added.forEach((id) => next.delete(id));
              removed.forEach((id) => next.add(id));
              return next;
            });
          }
        },
        [roundId, enqueueSnackbar],
      );

      // Limit modal state
      const [limitModalOpen, setLimitModalOpen] = useState(false);
      const closeLimitModal = useCallback(() => setLimitModalOpen(false), []);

      const isAtCapacity = selectedIds.size >= MAX_SELECTED_VENTURES;

      // Actions
      const handleAdd = useCallback(
        (id: VentureId) => {
          if (selectedIdsRef.current.size >= MAX_SELECTED_VENTURES) return;
          toggleSelection(id, true);
          leftRef.current.setCheckedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          saveChange([id], []);
        },
        [saveChange, toggleSelection],
      );

      const handleRemove = useCallback(
        (id: VentureId) => {
          toggleSelection(id, false);
          rightRef.current.setCheckedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          saveChange([], [id]);
        },
        [saveChange, toggleSelection],
      );

      const [isBulkProcessing, setIsBulkProcessing] = useState(false);

      const handleBulkAdd = useCallback(() => {
        const ids = Array.from(leftRef.current.checkedIds);
        if (selectedIdsRef.current.size + ids.length > MAX_SELECTED_VENTURES) {
          setLimitModalOpen(true);
          return;
        }
        setIsBulkProcessing(true);
        requestAnimationFrame(() => {
          setTimeout(() => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              ids.forEach((id) => next.add(id));
              return next;
            });
            leftRef.current.setCheckedIds(new Set());
            setIsBulkProcessing(false);
            saveChange(ids, []);
          }, 0);
        });
      }, [saveChange]);

      const handleBulkRemove = useCallback(() => {
        const ids = Array.from(rightRef.current.checkedIds);
        setIsBulkProcessing(true);
        requestAnimationFrame(() => {
          setTimeout(() => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              ids.forEach((id) => next.delete(id));
              return next;
            });
            rightRef.current.setCheckedIds(new Set());
            setIsBulkProcessing(false);
            saveChange([], ids);
          }, 0);
        });
      }, [saveChange]);

      // Shift+click range selection anchors
      const lastClickedLeftRef = useRef<VentureId | null>(null);
      const lastClickedRightRef = useRef<VentureId | null>(null);

      // Checkbox handlers — stable callbacks for list items
      const handleToggleCheckLeft = useCallback(
        (id: VentureId, shiftKey: boolean) => {
          if (shiftKey && lastClickedLeftRef.current !== null) {
            const filtered = leftRef.current.filtered;
            const anchorIdx = filtered.findIndex(
              (v) => v.id === lastClickedLeftRef.current,
            );
            const targetIdx = filtered.findIndex((v) => v.id === id);
            if (anchorIdx !== -1 && targetIdx !== -1) {
              const from = Math.min(anchorIdx, targetIdx);
              const to = Math.max(anchorIdx, targetIdx);
              const rangeIds = filtered.slice(from, to + 1).map((v) => v.id);
              leftRef.current.setCheckedIds((prev) => {
                const next = new Set(prev);
                rangeIds.forEach((rid) => next.add(rid));
                return next;
              });
              lastClickedLeftRef.current = id;
              return;
            }
          }
          leftRef.current.setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
          lastClickedLeftRef.current = id;
        },
        [],
      );

      const handleToggleCheckRight = useCallback(
        (id: VentureId, shiftKey: boolean) => {
          if (shiftKey && lastClickedRightRef.current !== null) {
            const filtered = rightRef.current.filtered;
            const anchorIdx = filtered.findIndex(
              (v) => v.id === lastClickedRightRef.current,
            );
            const targetIdx = filtered.findIndex((v) => v.id === id);
            if (anchorIdx !== -1 && targetIdx !== -1) {
              const from = Math.min(anchorIdx, targetIdx);
              const to = Math.max(anchorIdx, targetIdx);
              const rangeIds = filtered.slice(from, to + 1).map((v) => v.id);
              rightRef.current.setCheckedIds((prev) => {
                const next = new Set(prev);
                rangeIds.forEach((rid) => next.add(rid));
                return next;
              });
              lastClickedRightRef.current = id;
              return;
            }
          }
          rightRef.current.setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
          lastClickedRightRef.current = id;
        },
        [],
      );

      const toggleAll = useCallback(
        (
          checked: boolean,
          items: Venture[],
          setIds: React.Dispatch<React.SetStateAction<Set<VentureId>>>,
        ) => {
          if (checked) setIds(new Set(items.map((v) => v.id)));
          else
            setIds((prev) => {
              const next = new Set(prev);
              items.forEach((v) => next.delete(v.id));
              return next;
            });
        },
        [],
      );

      // Imperative handle
      useImperativeHandle(
        ref,
        () => ({
          getSelectedCount: () => selectedIds.size,
        }),
        [selectedIds],
      );

      const totalSelected = selectedIds.size;
      const totalAvailable = allVentures.length - totalSelected;

      // Render helpers
      const cohortMap = useMemo(() => {
        const map = new Map<CohortId, string>();
        cohorts.forEach((c) => map.set(c.id, c.name));
        return map;
      }, [cohorts]);

      const leftCohortOptions = useMemo(() => {
        const ids = new Set(
          availableRaw.map((v) => v.cohortId).filter(Boolean),
        );
        return cohorts
          .filter((c) => ids.has(c.id))
          .map((c) => ({ value: c.id, label: c.name }));
      }, [availableRaw, cohorts]);

      const rightCohortOptions = useMemo(() => {
        const ids = new Set(selectedRaw.map((v) => v.cohortId).filter(Boolean));
        return cohorts
          .filter((c) => ids.has(c.id))
          .map((c) => ({ value: c.id, label: c.name }));
      }, [selectedRaw, cohorts]);

      // Reset cohort filter when the selected cohort no longer has ventures
      // in that panel. Uses refs for setCohort to keep deps narrow.
      const leftCohort = left.filter.cohort;
      const rightCohort = right.filter.cohort;

      useEffect(() => {
        if (
          leftCohort !== 'all' &&
          !leftCohortOptions.some((o) => o.value === leftCohort)
        ) {
          leftRef.current.setCohort('all');
        }
      }, [leftCohortOptions, leftCohort]);

      useEffect(() => {
        if (
          rightCohort !== 'all' &&
          !rightCohortOptions.some((o) => o.value === rightCohort)
        ) {
          rightRef.current.setCohort('all');
        }
      }, [rightCohortOptions, rightCohort]);

      if (loadError) {
        return (
          <div className={classes.loader}>
            <AlertState type='error'>
              Failed to load ventures. Please try again.
            </AlertState>
          </div>
        );
      }

      const listSpinner = (
        <div className={classes.listLoader}>
          <CircularProgress size={32} />
        </div>
      );

      return (
        <div className={classes.panels} data-testid='venture-subgroup-selector'>
          {/* Left Panel */}
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.titleRow}>
                <span className={classes.panelTitle}>Available Ventures</span>
                <span className={classes.counter}>
                  (
                  <span className={classes.counterBold}>
                    {left.stats.checkedCount}
                  </span>{' '}
                  / {totalAvailable})
                </span>
              </div>
              <div className={classes.filterRow}>
                <DebouncedSearchInput
                  placeholder='Search available...'
                  onDebouncedChange={left.setSearch}
                  className={classes.searchField}
                  testId='search-available'
                />
                <Select
                  className={classes.cohortSelect}
                  value={left.filter.cohort}
                  options={[
                    { value: 'all', label: 'All Cohorts' },
                    ...leftCohortOptions,
                  ]}
                  onSelect={(item) =>
                    left.setCohort((item?.value || 'all') as CohortFilter)
                  }
                  isActive={left.filter.cohort !== 'all'}
                  data-testid='filter-left-select'
                />
              </div>
            </div>
            <div
              className={cn(classes.selectAllRow, {
                [classes.selectAllRowDisabled]: left.filtered.length === 0,
              })}
            >
              <Checkbox
                color='primary'
                size='small'
                checked={left.stats.allFilteredChecked}
                indeterminate={left.stats.someFilteredChecked}
                onChange={(e) =>
                  toggleAll(e.target.checked, left.filtered, left.setCheckedIds)
                }
                disabled={left.filtered.length === 0}
                data-testid='select-all-left'
              />
              <span>Select all filtered for bulk add</span>
            </div>
            {isLoading ? (
              listSpinner
            ) : (
              <VentureList
                ventures={left.filtered}
                checkedIds={left.checkedIds}
                cohortMap={cohortMap}
                onToggleCheck={handleToggleCheckLeft}
                onAction={handleAdd}
                actionDisabled={isAtCapacity}
                emptyMessage='No ventures match your filters'
                listTestId='list-available'
                itemTestIdPrefix='available-item-'
                actionTestIdPrefix='add-btn-'
                actionLabel='Add venture'
                variant='add'
              />
            )}
            <div className={classes.bulkBar}>
              <button
                type='button'
                className={classes.bulkBtnAdd}
                onClick={handleBulkAdd}
                disabled={
                  isLoading ||
                  isBulkProcessing ||
                  left.checkedIds.size === 0 ||
                  isAtCapacity
                }
                data-testid='btn-add-checked'
              >
                Add Selected
              </button>
            </div>
          </div>

          {/* Right Panel */}
          <div className={classes.panel}>
            <div className={classes.panelHeader}>
              <div className={classes.titleRow}>
                <span className={classes.panelTitle}>Selected Subgroup</span>
                <span className={classes.counter}>
                  (
                  <span className={classes.counterBold}>
                    {right.stats.checkedCount}
                  </span>{' '}
                  / {totalSelected})
                </span>
              </div>
              <div className={classes.filterRow}>
                <DebouncedSearchInput
                  placeholder='Search selected...'
                  onDebouncedChange={right.setSearch}
                  className={classes.searchField}
                  testId='search-selected'
                />
                <Select
                  className={classes.cohortSelect}
                  value={right.filter.cohort}
                  options={[
                    { value: 'all', label: 'All Cohorts' },
                    ...rightCohortOptions,
                  ]}
                  onSelect={(item) =>
                    right.setCohort((item?.value || 'all') as CohortFilter)
                  }
                  isActive={right.filter.cohort !== 'all'}
                  data-testid='filter-right-select'
                />
              </div>
            </div>
            <div
              className={cn(classes.selectAllRow, {
                [classes.selectAllRowDisabled]: right.filtered.length === 0,
              })}
            >
              <Checkbox
                color='primary'
                size='small'
                checked={right.stats.allFilteredChecked}
                indeterminate={right.stats.someFilteredChecked}
                onChange={(e) =>
                  toggleAll(
                    e.target.checked,
                    right.filtered,
                    right.setCheckedIds,
                  )
                }
                disabled={right.filtered.length === 0}
                data-testid='select-all-right'
              />
              <span>Select all filtered for bulk remove</span>
            </div>
            {isLoading ? (
              listSpinner
            ) : (
              <VentureList
                ventures={right.filtered}
                checkedIds={right.checkedIds}
                cohortMap={cohortMap}
                onToggleCheck={handleToggleCheckRight}
                onAction={handleRemove}
                emptyMessage='No ventures selected'
                listTestId='list-selected'
                itemTestIdPrefix='selected-item-'
                actionTestIdPrefix='remove-btn-'
                actionLabel='Remove venture'
                variant='remove'
              />
            )}
            <div className={classes.bulkBar}>
              <button
                type='button'
                className={classes.bulkBtnSecondary}
                onClick={handleBulkRemove}
                disabled={
                  isLoading || isBulkProcessing || right.checkedIds.size === 0
                }
                data-testid='btn-remove-checked'
              >
                Remove Selected
              </button>
            </div>
          </div>

          <ActionsDialog
            open={limitModalOpen}
            onClose={closeLimitModal}
            title='Exceeding Round Quota'
            actions={
              <Button
                onClick={closeLimitModal}
                variant='contained'
                data-testid='limit-dialog-ok'
              >
                Ok
              </Button>
            }
          >
            You cannot add more than {MAX_SELECTED_VENTURES} ventures to
            Selected Subgroup in a round
          </ActionsDialog>
        </div>
      );
    },
  ),
);

export default VentureSubgroupSelector;
